import React, { useState } from 'react';
import Icon from './AppIcon';

const PARTY_TYPES = [
  { value: 'attorney', label: 'Attorney' },
  { value: 'lender', label: 'Lender' },
  { value: 'inspector', label: 'Inspector' },
  { value: 'title_company', label: 'Title Company' },
  { value: 'appraiser', label: 'Appraiser' },
  { value: 'realtor', label: 'Realtor' },
  { value: 'other', label: 'Other' }
];

const PartiesSection = ({ buyerName, sellerName, parties = [], onUpdateParties, isReadOnly = false, clientType = null, onClientTypeChange = null }) => {
  const [isAddingParty, setIsAddingParty] = useState(false);
  const [newParty, setNewParty] = useState({ name: '', type: 'attorney', email: '', phone: '' });
  const [editingPartyId, setEditingPartyId] = useState(null);

  const handleAddParty = () => {
    if (!newParty.name.trim()) {
      return;
    }

    const party = {
      id: `party-${Date.now()}`,
      ...newParty,
      name: newParty.name.trim()
    };

    onUpdateParties([...parties, party]);
    setNewParty({ name: '', type: 'attorney', email: '', phone: '' });
    setIsAddingParty(false);
  };

  const handleRemoveParty = (partyId) => {
    if (!confirm('Remove this party?')) return;
    onUpdateParties(parties.filter(p => p.id !== partyId));
  };

  const handleUpdateParty = (partyId, field, value) => {
    onUpdateParties(
      parties.map(p =>
        p.id === partyId ? { ...p, [field]: value } : p
      )
    );
  };

  const getPartyTypeLabel = (type) => {
    return PARTY_TYPES.find(t => t.value === type)?.label || type;
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg mb-6">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-black flex items-center space-x-2">
            <Icon name="Users" size={20} />
            <span>Parties</span>
          </h2>
          {!isReadOnly && (
            <button
              onClick={() => setIsAddingParty(true)}
              disabled={isAddingParty}
              className="bg-black text-white px-4 py-2 rounded hover:bg-gray-800 transition-colors disabled:opacity-50 text-sm font-medium"
            >
              + Add Party
            </button>
          )}
        </div>
      </div>

      <div className="divide-y divide-gray-200">
        {/* Client Selection Instruction */}
        {(buyerName || sellerName) && !isReadOnly && (
          <div className="px-6 py-3 bg-gray-50">
            <p className="text-xs text-gray-600 font-medium">Select your client:</p>
          </div>
        )}

        {/* Buyer */}
        {buyerName && (
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3">
                  {!isReadOnly && (
                    <input
                      type="radio"
                      id="client-buyer"
                      name="clientType"
                      value="buyer"
                      checked={clientType === 'buyer'}
                      onChange={() => onClientTypeChange && onClientTypeChange('buyer')}
                      className="w-4 h-4 cursor-pointer"
                    />
                  )}
                  <label htmlFor="client-buyer" className={`flex items-center flex-1 ${!isReadOnly ? 'cursor-pointer' : ''}`}>
                    <div className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-semibold">
                      Buyer
                    </div>
                  </label>
                </div>
                <h3 className="text-lg font-semibold text-black mt-2">{buyerName}</h3>
              </div>
            </div>
          </div>
        )}

        {/* Seller */}
        {sellerName && (
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3">
                  {!isReadOnly && (
                    <input
                      type="radio"
                      id="client-seller"
                      name="clientType"
                      value="seller"
                      checked={clientType === 'seller'}
                      onChange={() => onClientTypeChange && onClientTypeChange('seller')}
                      className="w-4 h-4 cursor-pointer"
                    />
                  )}
                  <label htmlFor="client-seller" className={`flex items-center flex-1 ${!isReadOnly ? 'cursor-pointer' : ''}`}>
                    <div className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold">
                      Seller
                    </div>
                  </label>
                </div>
                <h3 className="text-lg font-semibold text-black mt-2">{sellerName}</h3>
              </div>
            </div>
          </div>
        )}

        {/* Additional Parties */}
        {parties.map((party) => (
          <div key={party.id} className="p-6">
            {editingPartyId === party.id && !isReadOnly ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={party.name}
                    onChange={(e) => handleUpdateParty(party.id, 'name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-black focus:ring-2 focus:ring-black focus:border-black"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Type</label>
                    <select
                      value={party.type}
                      onChange={(e) => handleUpdateParty(party.id, 'type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-black focus:ring-2 focus:ring-black focus:border-black"
                    >
                      {PARTY_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={party.email || ''}
                    onChange={(e) => handleUpdateParty(party.id, 'email', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-black focus:ring-2 focus:ring-black focus:border-black"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={party.phone || ''}
                    onChange={(e) => handleUpdateParty(party.id, 'phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-black focus:ring-2 focus:ring-black focus:border-black"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingPartyId(null)}
                    className="flex-1 px-3 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors text-sm font-medium"
                  >
                    Done
                  </button>
                  <button
                    onClick={() => handleRemoveParty(party.id)}
                    className="flex-1 px-3 py-2 border border-red-200 text-red-600 rounded hover:bg-red-50 transition-colors text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div
                className={`flex items-start justify-between ${!isReadOnly ? 'cursor-pointer hover:bg-gray-50 p-2 -m-2 rounded' : ''}`}
                onClick={() => !isReadOnly && setEditingPartyId(party.id)}
              >
                <div className="flex-1">
                  <div className="inline-block bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-xs font-semibold mb-2">
                    {getPartyTypeLabel(party.type)}
                  </div>
                  <h3 className="text-lg font-semibold text-black">{party.name}</h3>
                  <div className="mt-2 space-y-1">
                    {party.email && (
                      <div className="flex items-center space-x-2 text-sm">
                        <Icon name="Mail" size={14} />
                        <a
                          href={`mailto:${party.email}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {party.email}
                        </a>
                      </div>
                    )}
                    {party.phone && (
                      <div className="flex items-center space-x-2 text-sm">
                        <Icon name="Phone" size={14} />
                        <a
                          href={`tel:${party.phone}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {party.phone}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
                {!isReadOnly && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveParty(party.id);
                    }}
                    className="ml-4 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    <Icon name="X" size={18} />
                  </button>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Add New Party Form */}
        {isAddingParty && (
          <div className="p-6 bg-gray-50">
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  placeholder="Enter party name"
                  value={newParty.name}
                  onChange={(e) => setNewParty({ ...newParty, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-black focus:ring-2 focus:ring-black focus:border-black"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Type</label>
                  <select
                    value={newParty.type}
                    onChange={(e) => setNewParty({ ...newParty, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-black focus:ring-2 focus:ring-black focus:border-black"
                  >
                    {PARTY_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  placeholder="email@example.com"
                  value={newParty.email}
                  onChange={(e) => setNewParty({ ...newParty, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-black focus:ring-2 focus:ring-black focus:border-black"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Phone</label>
                <input
                  type="tel"
                  placeholder="(555) 000-0000"
                  value={newParty.phone}
                  onChange={(e) => setNewParty({ ...newParty, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-black focus:ring-2 focus:ring-black focus:border-black"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleAddParty}
                  className="flex-1 px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors font-medium"
                >
                  Add Party
                </button>
                <button
                  onClick={() => {
                    setIsAddingParty(false);
                    setNewParty({ name: '', type: 'attorney', email: '', phone: '' });
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded hover:bg-gray-100 transition-colors font-medium text-black"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {parties.length === 0 && !isAddingParty && (
        <div className="p-6 text-center text-gray-500">
          <Icon name="Users" size={32} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">No additional parties added yet.</p>
        </div>
      )}
    </div>
  );
};

export default PartiesSection;
